import { useCallback } from 'react'
import {
  Attributes,
  Experiment,
  useExperiment as useGrowthBookExperiment,
  useFeatureValue,
  useGrowthBook,
} from '@growthbook/growthbook-react'
import { AppFeatures } from './app-features'

type CypressWindow = typeof window & { Cypress?: { isTest?: boolean } }

export function useExperiment<T extends keyof AppFeatures>(
  key: T,
  defaultValue: AppFeatures[T]
) {
  const growthbook = useGrowthBook()
  const value = useFeatureValue<AppFeatures[T]>(key, defaultValue)

  // return default value if running in Cypress
  if (
    typeof window !== 'undefined' &&
    (window as CypressWindow).Cypress !== undefined
  ) {
    return [defaultValue, true]
  }

  return [value, growthbook.ready]
}

export function useExperimentContext() {
  const growthbook = useGrowthBook()

  const resolvedSetAttributes = useCallback(
    (attributes: Attributes) => {
      growthbook.setAttributes({ ...growthbook.getAttributes(), ...attributes })
    },
    [growthbook]
  )

  const getFeatureValue = useCallback(
    <T extends keyof AppFeatures>(key: T, defaultValue: AppFeatures[T]) => {
      return growthbook.getFeatureValue(key, defaultValue)
    },
    [growthbook]
  )

  return {
    getUserAttributes: growthbook.getAttributes,
    setUserAttributes: resolvedSetAttributes,
    getFeatureValue,
    ready: growthbook.ready,
  }
}

export function useInlineExperiment<T>(exp: Experiment<T>) {
  const { value } = useGrowthBookExperiment(exp)

  return value
}
